import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, Plus, Edit2, Trash2, X, Check, Loader2, 
  AlertCircle, MapPin, ChevronRight, FileText, Download, 
  Search, Phone, Mail, Clock, PlusCircle, MinusCircle, UserCheck
} from 'lucide-react';

const DEPARTMENTS = [
  'Hospital Equipment & OT Planning',
  'Pharmaceutical Distribution',
  'Ward & ICU Layouts Division',
  'Other / Corporate Office'
];

const JOB_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Internship'];

export default function ManageJobs() {
  // Database States
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingApps, setLoadingApps] = useState(true);
  const [activeTab, setActiveTab] = useState('posted-jobs'); // 'posted-jobs' or 'applications'
  
  // CRUD Job Posting Modal States
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [currentJob, setCurrentJob] = useState(null); // null for new
  const [savingJob, setSavingJob] = useState(false);

  // Job Form Fields
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState(DEPARTMENTS[0]);
  const [type, setType] = useState('Full-Time');
  const [location, setLocation] = useState('Sangli / Miraj');
  const [experience, setExperience] = useState('');
  const [desc, setDesc] = useState('');
  const [requirements, setRequirements] = useState(['']);

  // Applicant Drawer Modal (View Details)
  const [selectedApp, setSelectedApp] = useState(null);

  // Search/Filters
  const [appSearch, setAppSearch] = useState('');
  
  const [error, setError] = useState('');

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const snap = await getDocs(collection(db, 'jobs'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setJobs(list);
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoadingApps(true);
      const snap = await getDocs(collection(db, 'job_applications'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by submittedAt desc
      list.sort((a, b) => {
        const timeA = a.submittedAt?.seconds || 0;
        const timeB = b.submittedAt?.seconds || 0;
        return timeB - timeA;
      });
      setApplications(list);
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setLoadingApps(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, []);

  const openAddJobModal = () => {
    setCurrentJob(null);
    setTitle('');
    setDepartment(DEPARTMENTS[0]);
    setType('Full-Time');
    setLocation('Sangli / Miraj');
    setExperience('');
    setDesc('');
    setRequirements(['']);
    setError('');
    setJobModalOpen(true);
  };

  const openEditJobModal = (job) => {
    setCurrentJob(job);
    setTitle(job.title || '');
    setDepartment(job.department || DEPARTMENTS[0]);
    setType(job.type || 'Full-Time');
    setLocation(job.location || 'Sangli / Miraj');
    setExperience(job.experience || '');
    setDesc(job.desc || '');
    setRequirements(job.requirements && job.requirements.length > 0 ? [...job.requirements] : ['']);
    setError('');
    setJobModalOpen(true);
  };

  const handleAddRequirement = () => {
    setRequirements([...requirements, '']);
  };

  const handleRemoveRequirement = (idx) => {
    if (requirements.length === 1) return;
    const updated = [...requirements];
    updated.splice(idx, 1);
    setRequirements(updated);
  };

  const handleRequirementChange = (idx, val) => {
    const updated = [...requirements];
    updated[idx] = val;
    setRequirements(updated);
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title || !experience || !desc || !location) {
      setError("Job Title, Experience, Description, and Location are required.");
      return;
    }

    // Check for duplicate job title
    const isDuplicate = jobs.some(j => 
      j.title.trim().toLowerCase() === title.trim().toLowerCase() && 
      (!currentJob || j.id !== currentJob.id)
    );
    if (isDuplicate) {
      setError("A job opening with this title already exists.");
      return;
    }

    const filteredReqs = requirements.map(r => r.trim()).filter(r => r !== '');
    if (filteredReqs.length === 0) {
      setError("Please add at least one job requirement.");
      return;
    }

    const jobData = {
      title: title.trim(),
      department,
      type,
      location: location.trim(),
      experience: experience.trim(),
      desc: desc.trim(),
      requirements: filteredReqs,
      updatedAt: new Date()
    };

    try {
      setSavingJob(true);
      if (currentJob) {
        const docRef = doc(db, 'jobs', currentJob.id);
        await updateDoc(docRef, jobData);
        Swal.fire({
          title: 'Success',
          text: 'Job posting updated!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        jobData.createdAt = new Date();
        await addDoc(collection(db, 'jobs'), jobData);
        Swal.fire({
          title: 'Success',
          text: 'Job posting created!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }

      await fetchJobs();
      setTimeout(() => {
        setJobModalOpen(false);
      }, 1000);
    } catch (err) {
      console.error("Error saving job:", err);
      setError("Failed to save job posting.");
    } finally {
      setSavingJob(false);
    }
  };

  const handleJobDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoadingJobs(true);
      await deleteDoc(doc(db, 'jobs', id));
      setJobs(jobs.filter(j => j.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Job posting deleted!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting job:", err);
      setError("Failed to delete job posting.");
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleApplicationDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this application deletion!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoadingApps(true);
      await deleteDoc(doc(db, 'job_applications', id));
      setApplications(applications.filter(app => app.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Job application deleted!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting application:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete application. Please try again.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoadingApps(false);
    }
  };

  const handleDownloadResume = (resume) => {
    if (!resume || !resume.base64) {
      Swal.fire({
        title: 'Info',
        text: 'No resume file available for this applicant.',
        icon: 'info',
        confirmButtonColor: '#111111'
      });
      return;
    }
    const link = document.createElement('a');
    link.href = resume.base64;
    link.download = resume.name || 'Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredApps = applications.filter(app => 
    app.name?.toLowerCase().includes(appSearch.toLowerCase()) || 
    app.position?.toLowerCase().includes(appSearch.toLowerCase())
  );

  return (
    <div className="space-y-8 font-poppins">
      
      {/* Upper Panel Page Info */}
      <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Careers & Recruitment Portal</h1>
          <p className="text-xs text-gray-500">Manage internal job postings and review incoming candidate application files.</p>
        </div>
        <button 
          onClick={openAddJobModal}
          className="btn-primary py-2.5 px-5 text-sm shrink-0 self-start sm:self-center"
        >
          <Plus size={16} />
          Post New Job
        </button>
      </div>

      {/* Toggle Tab Options */}
      <div className="flex bg-white p-1 rounded-2xl border border-brand-silver/50 shadow-sm max-w-md">
        <button
          onClick={() => setActiveTab('posted-jobs')}
          className={`flex-grow flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold font-poppins transition-all duration-200 ${
            activeTab === 'posted-jobs'
              ? 'bg-brand-navy text-white shadow-sm'
              : 'text-gray-400 hover:text-brand-navy hover:bg-brand-light'
          }`}
        >
          <Briefcase size={14} />
          Posted Jobs ({jobs.length})
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`flex-grow flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold font-poppins transition-all duration-200 ${
            activeTab === 'applications'
              ? 'bg-brand-navy text-white shadow-sm'
              : 'text-gray-400 hover:text-brand-navy hover:bg-brand-light'
          }`}
        >
          <FileText size={14} />
          Job Applications ({applications.length})
        </button>
      </div>



      {/* Tab Content Display */}
      <div>
        {activeTab === 'posted-jobs' ? (
          <section className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6">
            <div className="border-b border-brand-silver pb-4">
              <h2 className="text-lg font-bold font-montserrat text-brand-navy flex items-center gap-2">
                <Briefcase size={18} className="text-brand-blue" />
                Active Job Postings
              </h2>
              <p className="text-xs text-gray-400">Total of {jobs.length} roles open to public candidates</p>
            </div>

            {loadingJobs ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 space-y-2">
                <Loader2 className="animate-spin text-brand-blue" size={28} />
                <span className="text-[10px] font-bold">Querying job postings...</span>
              </div>
            ) : jobs.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-6 text-center">No active job openings found in Firestore database.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 overflow-y-auto pr-1 max-h-[70vh]">
                {jobs.map((job) => (
                  <div 
                    key={job.id} 
                    className="p-5 rounded-2xl border border-brand-silver bg-brand-light flex flex-col justify-between hover:border-brand-blue/30 transition-all duration-200 group"
                  >
                    <div className="space-y-3">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <span className="text-[9px] font-bold text-brand-blue uppercase tracking-widest bg-brand-blue/10 px-2 py-0.5 rounded">
                            {job.type}
                          </span>
                          <h4 className="text-xs font-bold text-brand-navy font-montserrat mt-1">{job.title}</h4>
                        </div>
                        
                        <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                          <button 
                            type="button"
                            onClick={() => openEditJobModal(job)}
                            className="p-1 rounded bg-white border border-brand-silver text-gray-500 hover:text-brand-blue hover:border-brand-blue transition-colors"
                            title="Edit Job"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button 
                            type="button"
                            onClick={() => handleJobDelete(job.id)}
                            className="p-1 rounded bg-white border border-brand-silver text-gray-500 hover:text-red-500 hover:border-red-500 transition-colors"
                            title="Delete Job"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      <p className="text-[11px] text-gray-500 font-poppins leading-relaxed line-clamp-2">{job.desc}</p>
                      
                      <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-400 font-semibold pt-2 border-t border-brand-silver/50">
                        <span className="flex items-center gap-1"><MapPin size={10} /> {job.location}</span>
                        <span>Exp: <strong className="text-brand-navy">{job.experience}</strong></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-silver pb-4">
              <div>
                <h2 className="text-lg font-bold font-montserrat text-brand-navy flex items-center gap-2">
                  <FileText size={18} className="text-emerald-500" />
                  Candidate Applications
                </h2>
                <p className="text-xs text-gray-400">Review resumes and candidate cover notes</p>
              </div>
              
              {/* Search inputs */}
              <div className="relative max-w-xs w-full">
                <input 
                  type="text"
                  placeholder="Search candidate or position..."
                  value={appSearch}
                  onChange={(e) => setAppSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                />
                <Search size={12} className="absolute left-2.5 top-2.5 text-gray-400" />
              </div>
            </div>

            {loadingApps ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 space-y-2">
                <Loader2 className="animate-spin text-brand-blue" size={28} />
                <span className="text-[10px] font-bold">Querying candidate submissions...</span>
              </div>
            ) : filteredApps.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-6 text-center">No matching job applications logged in database.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 overflow-y-auto pr-1 max-h-[70vh]">
                {filteredApps.map((app) => (
                  <div 
                    key={app.id} 
                    className="p-5 rounded-2xl border border-brand-silver bg-brand-light flex flex-col justify-between hover:shadow-sm transition-all"
                  >
                    <div className="space-y-3">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <h4 className="text-xs font-bold text-brand-navy font-montserrat">{app.name}</h4>
                          <p className="text-[10px] text-brand-blue font-bold font-poppins uppercase tracking-wider mt-0.5">
                            Position: {app.position}
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className="text-[9px] text-gray-400 font-poppins font-medium">
                            {app.submittedAt ? new Date(app.submittedAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                          </span>
                          <button 
                            type="button"
                            onClick={() => handleApplicationDelete(app.id)}
                            className="p-1 rounded bg-white border border-brand-silver text-gray-500 hover:text-red-500 hover:border-red-500 transition-colors"
                            title="Delete Application"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      <p className="text-[11px] text-gray-500 font-poppins line-clamp-2 leading-relaxed">
                        {app.message || 'No additional note provided by the candidate.'}
                      </p>

                      <div className="flex flex-wrap gap-4 items-center justify-between pt-3 border-t border-brand-silver/50">
                        {/* Left: Contact Info */}
                        <div className="flex flex-wrap gap-3 text-[10px] text-gray-400 font-semibold font-poppins">
                          <span className="flex items-center gap-1"><Phone size={10} /> {app.phone}</span>
                          {app.email && <span className="flex items-center gap-1"><Mail size={10} /> {app.email}</span>}
                          <span>Exp: <strong className="text-brand-navy">{app.experience}</strong></span>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex gap-2">
                          <button 
                            type="button"
                            onClick={() => setSelectedApp(app)}
                            className="px-3 py-1 bg-white hover:bg-brand-blue hover:text-white border border-brand-silver text-brand-navy rounded-lg text-[10px] font-bold font-poppins flex items-center gap-1 transition-colors"
                          >
                            View Detail
                          </button>
                          
                          {app.resume && (
                            <button 
                              type="button"
                              onClick={() => handleDownloadResume(app.resume)}
                              className="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-[10px] font-bold font-poppins flex items-center gap-1 transition-colors shadow-sm"
                            >
                              <Download size={10} /> Download Resume
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* CRUD Job Posting Overlay Modal */}
      <AnimatePresence>
        {jobModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !savingJob && setJobModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col max-h-[90vh]"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentJob ? 'Edit Job Posting' : 'Post New Job'}
                  </h2>
                  <p className="text-xs text-gray-500">Provide configuration details for careers board integration.</p>
                </div>
                <button 
                  onClick={() => setJobModalOpen(false)}
                  disabled={savingJob}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleJobSubmit} className="flex-grow overflow-y-auto p-6 space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 text-xs font-semibold rounded-r-xl flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Job Title */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Job Title</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. Biomedical Sales Engineer"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>

                  {/* Department */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Corporate Division</label>
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
                    >
                      {DEPARTMENTS.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Job Type */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Job Type</label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
                    >
                      {JOB_TYPES.map(jt => (
                        <option key={jt} value={jt}>{jt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Location */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Work Location</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. Sangli / Miraj / Kolhapur"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>

                  {/* Experience */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Required Experience</label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. 2 - 5 Years"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                {/* Job Description */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Job Summary / Description</label>
                  <textarea 
                    rows={3}
                    required
                    placeholder="Provide a description of the roles and day-to-day operations..."
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins resize-none leading-relaxed"
                  />
                </div>

                {/* Requirements Array */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins font-semibold">Specific Job Requirements</label>
                    <button 
                      type="button" 
                      onClick={handleAddRequirement}
                      className="text-xs text-brand-blue hover:text-brand-navy flex items-center gap-1 font-bold"
                    >
                      <PlusCircle size={14} /> Add Requirement
                    </button>
                  </div>

                  <div className="space-y-2">
                    {requirements.map((req, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <span className="text-xs font-bold text-gray-400 shrink-0 w-4">{idx + 1}.</span>
                        <input 
                          type="text"
                          required
                          placeholder="e.g. Strong communication and local client management skills."
                          value={req}
                          onChange={(e) => handleRequirementChange(idx, e.target.value)}
                          className="flex-grow px-4 py-2 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                        />
                        <button 
                          type="button"
                          disabled={requirements.length === 1}
                          onClick={() => handleRemoveRequirement(idx)}
                          className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                        >
                          <MinusCircle size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button 
                    type="button"
                    disabled={savingJob}
                    onClick={() => setJobModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={savingJob}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {savingJob ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Saving posting...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentJob ? 'Save Changes' : 'Create Job'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Applicant Detailed Inspector Modal */}
      <AnimatePresence>
        {selectedApp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedApp(null)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col"
            >
              {/* Header */}
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">Candidate Application Profile</h2>
                  <p className="text-xs text-gray-500">Recruitment files and cover notes log.</p>
                </div>
                <button 
                  onClick={() => setSelectedApp(null)}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Details Body */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                
                {/* Upper Name & Position */}
                <div className="flex gap-4 p-5 rounded-2xl bg-brand-light border border-brand-silver/60">
                  <div className="w-12 h-12 rounded-xl bg-brand-blue/15 text-brand-blue flex items-center justify-center font-bold text-lg shrink-0">
                    {selectedApp.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold font-montserrat text-brand-navy">{selectedApp.name}</h3>
                    <p className="text-xs text-brand-blue font-bold font-poppins uppercase tracking-wider mt-0.5">
                      Applying for: {selectedApp.position}
                    </p>
                    <span className="text-[10px] text-gray-400 font-poppins flex items-center gap-1 mt-1">
                      <Clock size={12} /> Logged: {selectedApp.submittedAt ? new Date(selectedApp.submittedAt.seconds * 1000).toLocaleString() : 'Just now'}
                    </span>
                  </div>
                </div>

                {/* Candidate Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Candidate Email</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins flex items-center gap-1.5"><Mail size={12} className="text-gray-400" /> {selectedApp.email || 'N/A'}</span>
                  </div>
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Phone Contacts</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins flex items-center gap-1.5"><Phone size={12} className="text-gray-400" /> {selectedApp.phone}</span>
                  </div>
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1 sm:col-span-2">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Required Experience</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins flex items-center gap-1.5"><UserCheck size={12} className="text-gray-400" /> Candidate claims: {selectedApp.experience}</span>
                  </div>
                </div>

                {/* Candidate Cover Letter */}
                <div className="space-y-1.5">
                  <label className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider">Candidate Cover Letter / Note</label>
                  <div className="p-4 rounded-xl bg-brand-light border border-brand-silver text-xs text-gray-600 leading-relaxed font-poppins whitespace-pre-wrap">
                    {selectedApp.message || 'No additional note provided by the candidate.'}
                  </div>
                </div>

                {/* Resume Download Section */}
                {selectedApp.resume && (
                  <div className="p-4 rounded-2xl border border-emerald-500/20 bg-emerald-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                        <FileText size={20} />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-brand-navy font-poppins truncate max-w-[200px]">{selectedApp.resume.name}</h4>
                        <p className="text-[10px] text-gray-500 font-poppins">Size: {selectedApp.resume.sizeKB} KB</p>
                      </div>
                    </div>

                    <button 
                      onClick={() => handleDownloadResume(selectedApp.resume)}
                      className="btn-green py-2 px-4 text-xs font-bold font-poppins shadow-sm"
                    >
                      <Download size={12} /> Download Resume
                    </button>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-6 border-t border-brand-silver/50 flex justify-between items-center">
                <button 
                  onClick={() => {
                    handleApplicationDelete(selectedApp.id);
                    setSelectedApp(null);
                  }}
                  className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-colors border border-red-200"
                >
                  <Trash2 size={14} /> Delete Application
                </button>
                <button 
                  onClick={() => setSelectedApp(null)}
                  className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                >
                  Close Profile
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
